import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { Chip } from "@/components/Chip";
import { fmtTime, monthOf, daysInMonth } from "@/lib/date";
import { MapPin, Wrench } from "lucide-react";

const DAYS = ["S", "M", "T", "W", "T", "F", "S"];

export default function Attendance() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const [month, setMonth] = useState(monthOf());
  const [userId, setUserId] = useState(""); // admin filter
  const [users, setUsers] = useState([]);
  const [att, setAtt] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [holidays, setHolidays] = useState([]);

  const targetId = (user?.role === "admin" && userId) ? userId : user?.user_id;

  const load = async () => {
    if (!user) return;
    try {
      const [a, l, h, u] = await Promise.all([
        api.get(`/attendance?month=${month}${user.role === "admin" && userId ? `&user_id=${userId}` : ""}`),
        api.get("/leaves"),
        api.get("/holidays"),
        api.get("/users"),
      ]);
      setAtt(a.data);
      setLeaves(l.data);
      setHolidays(h.data);
      setUsers(u.data);
    } catch (e) {/*ignore*/}
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user, month, userId]);

  const days = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const total = daysInMonth(month);
    const first = new Date(y, m - 1, 1).getDay();
    const arr = [];
    for (let i = 0; i < first; i++) arr.push(null);
    for (let d = 1; d <= total; d++) {
      const iso = `${month}-${String(d).padStart(2, "0")}`;
      const dt = new Date(y, m - 1, d);
      const dow = dt.getDay();
      const isFuture = dt > new Date();
      const myAtt = att.find((a) => a.user_id === targetId && a.date === iso);
      const myLeave = leaves.find((l) => l.user_id === targetId && l.date === iso && l.status === "approved");
      const isHol = holidays.find((h) => h.date === iso);
      let status = "F";
      if (isHol) status = "HO";
      else if (myLeave) status = "L";
      else if (myAtt?.check_in && myAtt?.check_out) status = "P";
      else if (myAtt?.check_in) status = "H";
      else if (dow === 0 && (settings?.sundays_optional ?? true)) status = "S";
      else if (!isFuture) status = "A";
      arr.push({ iso, day: d, dow, status, att: myAtt, leave: myLeave, holiday: isHol });
    }
    return arr;
  }, [month, att, leaves, holidays, settings, targetId]);

  const counts = useMemo(() => {
    const c = { P: 0, A: 0, H: 0, HO: 0, S: 0, L: 0, F: 0 };
    days.forEach((d) => { if (d) c[d.status] = (c[d.status] || 0) + 1; });
    return c;
  }, [days]);

  const COLOR = { P: "#22D3A5", A: "#F75A5A", H: "#F7C94F", HO: "#9B6FEB", S: "#3A4060", L: "#4F8EF7", F: "#1E2640" };

  return (
    <div className="px-4 pt-3 pb-4 space-y-4" data-testid="attendance-page">
      <div className="flex gap-2">
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          data-testid="month-picker"
          className="flex-1 h-11 px-3 rounded-xl font-mono text-sm"
          style={{ background: "#161B2C", border: "1px solid #1E2640", color: "#E8ECFF" }}
        />
        {user?.role === "admin" && (
          <select
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            data-testid="user-filter"
            className="flex-1 h-11 px-3 rounded-xl text-sm"
            style={{ background: "#161B2C", border: "1px solid #1E2640", color: "#E8ECFF" }}
          >
            <option value="">Me</option>
            {users.filter((u) => u.user_id !== user.user_id).map((u) => (
              <option key={u.user_id} value={u.user_id}>{u.name}</option>
            ))}
          </select>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2">
        {[["Present","P","#22D3A5"],["Absent","A","#F75A5A"],["Half","H","#F7C94F"],["Leave","L","#4F8EF7"],["Holiday","HO","#9B6FEB"],["Sunday","S","#7A85A3"]].map(([l, k, c]) => (
          <div key={k} className="rounded-2xl p-2.5" style={{ background: "#161B2C", border: "1px solid #1E2640" }}>
            <div className="font-syne font-bold text-xl" style={{ color: c }}>{counts[k] || 0}</div>
            <div className="text-[10px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>{l}</div>
          </div>
        ))}
      </div>

      {/* Calendar */}
      <div className="rounded-3xl p-4" style={{ background: "#161B2C", border: "1px solid #1E2640" }} data-testid="calendar">
        <div className="grid grid-cols-7 gap-1 mb-2">
          {DAYS.map((d, i) => (
            <div key={i} className="text-center text-[10px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days.map((d, i) => (
            <div key={i} className="aspect-square rounded-lg flex flex-col items-center justify-center relative"
              style={{ background: d ? `${COLOR[d.status]}1F` : "transparent", border: d ? `1px solid ${COLOR[d.status]}55` : "1px solid transparent" }}
              title={d ? `${d.iso} · ${d.status}${d.att ? ` · ${fmtTime(d.att.check_in)} - ${fmtTime(d.att.check_out)}` : ""}` : ""}
              data-testid={d ? `cal-${d.iso}` : undefined}
            >
              {d && <>
                <div className="text-[10px]" style={{ color: "#7A85A3" }}>{d.day}</div>
                <div className="text-[10px] font-bold" style={{ color: COLOR[d.status] }}>{d.status}</div>
              </>}
            </div>
          ))}
        </div>
      </div>

      {/* Daily log */}
      <div>
        <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: "#7A85A3" }}>Daily Log</div>
        <div className="space-y-2">
          {days.filter(Boolean).reverse().slice(0, 31).map((d) => (
            <div key={d.iso} className="rounded-xl p-3 flex items-center gap-3" style={{ background: "#161B2C", border: "1px solid #1E2640" }} data-testid={`log-${d.iso}`}>
              <div className="text-center w-10">
                <div className="font-syne font-bold text-lg" style={{ color: "#E8ECFF" }}>{d.day}</div>
                <div className="text-[9px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>{DAYS[d.dow]}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Chip color={d.status === "P" ? "green" : d.status === "A" ? "red" : d.status === "H" ? "amber" : d.status === "L" ? "blue" : d.status === "HO" ? "purple" : "gray"}>{d.status}</Chip>
                  {d.att?.regularized && <Chip color="blue" icon={<Wrench size={10} />}>Reg</Chip>}
                  {d.att?.check_in_lat && <MapPin size={12} style={{ color: "#7A85A3" }} />}
                </div>
                <div className="text-[11px] mt-0.5 font-mono" style={{ color: "#7A85A3" }}>
                  {d.att?.check_in ? `${fmtTime(d.att.check_in)} → ${d.att?.check_out ? fmtTime(d.att.check_out) : "—"}` : d.holiday?.name || (d.leave ? `On leave (${d.leave.type})` : d.status === "S" ? "Sunday" : "—")}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
